import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import UploadModal from '../../components/admin/UploadModal';
import DocumentsService from '../../services/documentsService';
import { useAuth } from '../../contexts/AuthContext';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

const AdminDocuments = () => {
  const { user, isAuthenticated, token, hasRole } = useAuth();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);
  const [showPublicOnly, setShowPublicOnly] = useState(false);

  // Fetch documents from the database
  const fetchDocuments = async () => {
    setLoading(true);
    setError(null);
    setSuccessMessage(null);
    
    try {
      const data = await DocumentsService.getAllDocuments();
      setDocuments(Array.isArray(data) ? data : []);
      setShowPublicOnly(false);
    } catch (error) {
      console.error('Error fetching admin documents:', error);
      // Fallback to public documents
      try {
        const publicDocuments = await DocumentsService.getActiveDocuments();
        setDocuments(publicDocuments);
        setShowPublicOnly(true);
        setSuccessMessage('Showing public documents only. Admin access may be limited.');
        setTimeout(() => setSuccessMessage(null), 5000);
      } catch (publicError) {
        console.error('Error fetching public documents:', publicError);
        setError('Failed to load documents');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleUploadSuccess = (uploadedDocument) => {
    // Refresh the documents list
    fetchDocuments();
    setSuccessMessage(`Document "${uploadedDocument.title}" uploaded successfully!`);
    // Clear success message after 5 seconds
    setTimeout(() => setSuccessMessage(null), 5000);
  };

  // Load documents when component mounts
  useEffect(() => {
    fetchDocuments();
  }, []);

  // Handle document view/download using the updated service
  const handleView = async (doc) => {
    try {
      console.log('📥 Admin downloading document:', doc.id, doc.title);
      
      // Use the updated documentsService which handles everything
      const result = await DocumentsService.downloadDocument(doc.id);
      
      // Check if download was successful
      if (result && result.success) {
        const method = result.method === 'redirect' ? 'S3 signed URL' : 'direct file';
        console.log(`✅ Admin download successful using ${method}`);
        
        // Optional: Show success feedback
        // You could add a toast notification here if desired
      } else {
        console.warn('⚠️ Download completed but status is unclear');
      }
      
    } catch (error) {
      console.error('❌ Admin download error:', error);
      
      // Show user-friendly error message
      const errorMessage = error.message || 'Error downloading document. Please try again.';
      alert(errorMessage);
    }
  };

  // Handle document deletion
  const handleDelete = async (id, title) => {
    if (!window.confirm(`Are you sure you want to delete "${title}"? This action cannot be undone.`)) {
      return;
    }
    
    try {
      await DocumentsService.deleteDocument(id);
      
      // Refresh the documents list
      await fetchDocuments();
      setSuccessMessage('Document deleted successfully!');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (error) {
      console.error('Error deleting document:', error);
      setError('Failed to delete document. Please try again.');
      setTimeout(() => setError(null), 3000);
    }
  };

  // Format file size for display
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Get file type icon based on MIME type
  const getFileTypeIcon = (mimeType) => {
    if (mimeType?.includes('pdf')) return '📄';
    if (mimeType?.includes('word') || mimeType?.includes('document')) return '📝';
    if (mimeType?.includes('sheet') || mimeType?.includes('excel')) return '📊';
    if (mimeType?.includes('presentation') || mimeType?.includes('powerpoint')) return '📈';
    if (mimeType?.includes('text')) return '📃';
    return '📄';
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <div className="flex justify-between items-center mb-4">
          <h1 className="text-3xl font-bold text-gray-900">Documents</h1>
          <button
            onClick={() => setShowUploadModal(true)}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Add Document
          </button>
        </div>
        <p className="text-gray-600 mt-2">View all documents from the database</p>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading documents...</p>
          </div>
        </div>
      )}

      {/* Error State */}
      {error && !loading && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <div className="text-red-500 text-6xl mb-4">⚠️</div>
          <h3 className="text-lg font-medium text-red-800 mb-2">Error Loading Documents</h3>
          <p className="text-red-600 mb-4">{error}</p>
          <button 
            onClick={fetchDocuments}
            className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      )}

      {/* Documents Table */}
      {!loading && !error && (
        <div className="bg-white shadow-sm rounded-lg border overflow-hidden">
          {documents.length === 0 ? (
            <div className="px-6 py-12 text-center text-gray-500">
              <div className="text-6xl mb-4">📄</div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No documents found</h3>
              <p className="text-gray-600">No documents are currently stored in the database</p>
            </div>
          ) : (
            <>
              <div className="px-6 py-4 bg-gray-50 border-b">
                <h3 className="text-lg font-medium text-gray-900">
                  Documents ({documents.length})
                </h3>
              </div>
              
              {/* Table Header */}
               <div className="bg-gray-50 px-6 py-3 border-b">
                 <div className="grid grid-cols-12 gap-4 text-sm font-medium text-gray-700">
                   <div className="col-span-1">Type</div>
                   <div className="col-span-4">Title</div>
                   <div className="col-span-3">File Name</div>
                   <div className="col-span-2">Size</div>
                   <div className="col-span-1">Created</div>
                   <div className="col-span-1">Actions</div>
                 </div>
               </div>
              
              {/* Table Body */}
              <div className="divide-y divide-gray-200">
                {documents.map((document) => (
                  <div key={document.id} className="px-6 py-4 hover:bg-gray-50 transition-colors">
                    <div className="grid grid-cols-12 gap-4 items-center">
                      <div className="col-span-1">
                        <span className="text-2xl">{getFileTypeIcon(document.mimeType)}</span>
                      </div>
                      <div className="col-span-4">
                        <h4 className="text-sm font-medium text-gray-900">{document.title}</h4>
                        {document.description && (
                          <p className="text-xs text-gray-500 mt-1">{document.description}</p>
                        )}
                      </div>
                      <div className="col-span-3">
                        <p className="text-sm text-gray-600">{document.originalName}</p>
                        <p className="text-xs text-gray-400">{document.mimeType}</p>
                      </div>
                      <div className="col-span-2">
                        <p className="text-sm text-gray-600">{formatFileSize(document.fileSize)}</p>
                      </div>
                      <div className="col-span-1">
                         <p className="text-sm text-gray-600">
                           {document.createdAt ? format(new Date(document.createdAt), 'MMM dd, yyyy') : 'Unknown'}
                         </p>
                         <p className="text-xs text-gray-400">
                           {document.createdAt ? format(new Date(document.createdAt), 'HH:mm') : ''}
                         </p>
                       </div>
                       <div className="col-span-1">
                         <div className="flex space-x-2">
                           <button
                             onClick={() => handleView(document)}
                             className="p-1 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded transition-colors"
                             title="View/Download Document"
                           >
                             <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                               <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                               <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                             </svg>
                           </button>
                           <button
                             onClick={() => handleDelete(document.id, document.title)}
                             className="p-1 text-red-600 hover:text-red-800 hover:bg-red-50 rounded transition-colors"
                             title="Delete Document"
                           >
                             <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                               <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                             </svg>
                           </button>
                         </div>
                       </div>
                    </div>
                    
                    {/* Additional Info Row */}
                    <div className="mt-2 flex items-center space-x-4 text-xs text-gray-500">
                      <span>ID: {document.id}</span>
                      {document.downloadCount > 0 && (
                        <span>Downloads: {document.downloadCount}</span>
                      )}
                      {document.isActive !== undefined && (
                        <span className={`px-2 py-1 rounded-full ${
                          document.isActive 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {document.isActive ? 'Active' : 'Inactive'}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {/* Success Message */}
      {successMessage && (
        <div className="fixed top-4 right-4 bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded z-50">
          <div className="flex items-center gap-2">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            {successMessage}
          </div>
        </div>
      )}

      {/* Upload Modal */}
      <UploadModal
        isOpen={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        onUpload={handleUploadSuccess}
      />
    </div>
  );
};

export default AdminDocuments;